import { Check, GitBranch } from "lucide-react";
import type { DecisionOption } from "@/lib/blocks";
import { cn } from "@/lib/utils";
import { Markdown } from "@/components/Markdown";

function chosenIndex(options: DecisionOption[], chosen: unknown): number {
  if (chosen === undefined || chosen === null || chosen === "") return -1;
  const c = String(chosen).trim();
  const byId = options.findIndex((o) => o.id !== undefined && String(o.id) === c);
  if (byId >= 0) return byId;
  const byLabel = options.findIndex((o) => String(o.label).trim() === c);
  if (byLabel >= 0) return byLabel;
  return options.findIndex((o) => String(o.label).trim().toLowerCase() === c.toLowerCase());
}

export function Decision({ props }: { props: Record<string, unknown> }) {
  const question = typeof props.question === "string" ? props.question : "";
  const options: DecisionOption[] = Array.isArray(props.options)
    ? (props.options as unknown[]).map((o) =>
        o && typeof o === "object"
          ? {
              id: (o as DecisionOption).id !== undefined ? String((o as DecisionOption).id) : undefined,
              label: String((o as DecisionOption).label ?? ""),
              note: (o as DecisionOption).note !== undefined ? String((o as DecisionOption).note) : undefined,
            }
          : { label: String(o) },
      )
    : [];
  const chosen = chosenIndex(options, props.chosen);
  const rationale = typeof props.rationale === "string" ? props.rationale : "";

  return (
    <section className="overflow-hidden rounded-xl border bg-card">
      <header className="flex items-start gap-3 px-4 py-3.5">
        <GitBranch className="mt-0.5 size-[18px] shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0">
          <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Decision</div>
          <div className="mt-0.5 text-[0.95rem] font-semibold leading-snug">
            {question ? <Markdown inline>{question}</Markdown> : <span className="text-muted-foreground">No question</span>}
          </div>
        </div>
      </header>
      {options.length > 0 && (
        <ul className="border-t">
          {options.map((o, i) => {
            const picked = i === chosen;
            return (
              <li
                key={o.id ?? i}
                className={cn(
                  "flex items-start gap-3 border-b px-4 py-2.5 last:border-b-0",
                  picked ? "bg-primary/[0.06]" : "text-muted-foreground",
                )}
              >
                <span
                  className={cn(
                    "mt-[0.35rem] inline-flex size-4 shrink-0 items-center justify-center rounded-full border",
                    picked ? "border-primary bg-primary text-primary-foreground" : "border-input",
                  )}
                  aria-hidden
                >
                  {picked && <Check className="size-3" strokeWidth={3} />}
                </span>
                <div className="min-w-0 flex-1 text-[0.95rem] leading-relaxed">
                  <span className={cn(picked && "font-medium text-foreground")}>
                    <Markdown inline>{o.label}</Markdown>
                  </span>
                  {o.note && (
                    <span className="text-muted-foreground">
                      {" "}
                      — <Markdown inline>{o.note}</Markdown>
                    </span>
                  )}
                </div>
                {picked && <span className="sr-only">(chosen)</span>}
              </li>
            );
          })}
        </ul>
      )}
      {rationale && (
        <div className="border-t bg-muted/30 px-4 py-3 text-sm">
          <span className="font-medium">Why: </span>
          <Markdown inline>{rationale}</Markdown>
        </div>
      )}
    </section>
  );
}
